import classNames from "classnames";
import { openDownloadUrl } from "util/openDownloadUrl";
import { Button } from "components/DesignSystem/Button/Button";
import Table from "components/DesignSystem/Table/V2/Table";
import { Preview } from "components/PreviewModal";
import { DownloadIcon } from "components/icons/Download";
import { DeleteIcon } from "components/icons/delete";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetAllCapTablesQuery } from "store/apis/capTable";
import { useLazyGetPreviewUrlQuery } from "store/apis/previewUrl";
import { CapTable as CapTableType } from "types/Models/capTable";
import { fileAndFolder } from "types/Models/documents";
import { BackendError } from "types/utils/error";
import { FileIcon } from "components/design/FileIcon";
import { CapTableSlider } from "./CapTableSlider";
import ToolTip from "components/design/toolTip";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { debounce } from "utils/debouncing";
import { Search } from "components/DesignSystem/Search/Search";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { DeleteModal } from "./DeleteModal";
import { useParams } from "react-router-dom";

const CapTablePreview = ({ document }: { document: fileAndFolder }) => {
  const { uuid: groupId } = useCurrentGroupContext();

  const {
    isOpen: isPreviewModalOpen,
    close: onPreviewClose,
    open: openPreview,
  } = useModal();

  const { uuid: previewId, file_type } = document;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <ToolTip text="Preview" side="top">
        <span>
          <Button
            customType="ghost_icon"
            size="xsmall"
            onClick={(e) => {
              openPreview();
            }}
          >
            <FileIcon fileType={file_type} size="24" />
          </Button>
        </span>
      </ToolTip>
      {isPreviewModalOpen && (
        <Preview
          showModal={isPreviewModalOpen}
          closeModal={onPreviewClose}
          groupId={groupId}
          previewId={previewId}
        />
      )}
    </div>
  );
};

const Actions = ({
  capTableId,
  fileId,
}: {
  capTableId: string;
  fileId: string;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { alertToast } = useToast();

  const { isOpen, open, close } = useModal();

  const [getPreviewUrl] = useLazyGetPreviewUrlQuery();

  const onDownload = async () => {
    try {
      const { download_url } = await getPreviewUrl({
        groupId: groupId,
        fileId: fileId,
      }).unwrap();
      openDownloadUrl(download_url);
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    }
  };

  return (
    <>
      <div
        className="t-flex t-gap-2 t-items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <ToolTip text="Download">
          <span>
            <Button customType="ghost_icon" size="xsmall" onClick={onDownload}>
              <DownloadIcon color="currentColor" />
            </Button>
          </span>
        </ToolTip>
        <ToolTip text="Delete">
          <span>
            <Button customType="ghost_icon" size="xsmall" onClick={open}>
              <DeleteIcon />
            </Button>
          </span>
        </ToolTip>
      </div>
      {isOpen && (
        <DeleteModal isOpen={isOpen} close={close} capTableId={capTableId} />
      )}
    </>
  );
};

export const CapTable = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { entityId } = useParams<{ entityId: string }>();
  const [capTableId, setCapTableId] = useState("");
  const { updateMultiple } = useUpdateQuery();
  const query = useQuery();
  const searchTerm = query.get("search_term") || null;
  const [pageNum, setPageNum] = useState(1);

  const { data: capTable } = useGetAllCapTablesQuery({
    pageNum: pageNum,
    searchTerm,
    groupId,
    entityId,
  });

  const {
    cap_tables: capTableData = [],
    current_page = 1,
    per_page = 1,
    total_count = 1,
    total_pages = 1,
  } = capTable || {};

  const handleChange = debounce((e) => {
    const { value } = e.target;
    updateMultiple([
      { query: "search_term", value: value || null },
      { query: "page", value: 1 },
    ]);
  });

  const onRowClick = (id: string) => {
    setCapTableId(id);
  };

  const createColumn = createColumnHelper<CapTableType>();

  const columns = useMemo(
    () => [
      createColumn.accessor("valid_as_on", {
        size: 12,
        header: "AS ON",
        cell: (info) => {
          const {
            row: {
              original: { valid_as_on },
            },
          } = info;

          return (
            <div className="t-text-subtext">
              {dayjs(valid_as_on).format(DISPLAY_DATE)}
            </div>
          );
        },
      }),

      createColumn.accessor("note", {
        size: 70,
        header: "NOTE",
        cell: (info) => {
          const {
            row: {
              original: { note },
            },
          } = info;

          return (
            <>
              {note ? (
                <ToolTip text={note} side="top">
                  <span>
                    <div className="t-text-subtext t-text-text-60 t-w-[730px] t-truncate">
                      {note}
                    </div>
                  </span>
                </ToolTip>
              ) : (
                "-"
              )}
            </>
          );
        },
      }),

      createColumn.accessor("document", {
        size: 10,
        header: "FILE",
        cell: (info) => {
          const {
            row: {
              original: { document },
            },
          } = info;

          return <CapTablePreview document={document} />;
        },
      }),

      createColumn.display({
        size: 5,
        header: "ACTIONS",
        cell: (info) => {
          const {
            row: {
              original: {
                uuid,
                document: { uuid: fileId },
              },
            },
          } = info;
          return <Actions capTableId={uuid} fileId={fileId} />;
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: capTableData || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: 10,
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <div>
      <div className="t-flex t-justify-between t-items-center">
        <div className="t-w-1/2 t-flex t-items-center">
          <Search
            placeholder="Search..."
            defaultValue={searchTerm || ""}
            onChange={handleChange}
            block
          />
        </div>
        <PaginationHandler
          onPageNumChange={(page) => setPageNum(page)}
          page_num={current_page}
          per_page={per_page}
          total_count={total_count}
          total_pages={total_pages}
        />
      </div>
      <div className="t-py-5">
        <Table.Container>
          <Table.Content>
            <Table.Head>
              {table.getHeaderGroups().map((headerGroup) => (
                <Table.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <Table.HeadCell
                      key={header.id}
                      style={{ width: `${header.getSize()}%` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </Table.HeadCell>
                  ))}
                </Table.Row>
              ))}
            </Table.Head>
            <Table.Body>
              {table.getRowModel().rows.map((row) => (
                <Table.Row
                  key={row.id}
                  onClick={() => onRowClick(row.original.uuid)}
                  className={classNames(
                    "hover:t-bg-surface-lighter-grey t-cursor-pointer",
                    {
                      "t-bg-surface-lighter-grey":
                        capTableId === row.original.uuid,
                    }
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <Table.Cell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}%` }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Content>
        </Table.Container>
        <CapTableSlider capTableId={capTableId} setCapTableId={setCapTableId} />
      </div>
    </div>
  );
};
